import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { FredrickLocalCaptureSummaryV1 } from 'fredrick-local-repository';
import { StatusPill } from '../components/StatusPill';
import { LibraryNav } from '../components/library/LibraryNav';
import { dateTime } from '../lib/format';
import { localRepository } from '../lib/repository';

function shortId(value: string): string {
  return value.length <= 26 ? value : `${value.slice(0, 18)}…${value.slice(-7)}`;
}

export function CaptureLibraryPage() {
  const [captures, setCaptures] = useState<readonly FredrickLocalCaptureSummaryV1[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [clearing, setClearing] = useState(false);

  const [refreshToken, setRefreshToken] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const next = await localRepository.listCaptures();
        if (cancelled) return;
        setCaptures(next);
        setError(null);
      } catch (value) {
        if (!cancelled) setError(value instanceof Error ? value.message : 'Could not read the local capture inventory.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void load();
    const unsubscribe = localRepository.subscribe(() => void load());
    return () => { cancelled = true; unsubscribe(); };
  }, [refreshToken]);

  async function clearLibrary() {
    if (!window.confirm('Delete every locally stored Fredrick capture, private Owl crop, derived market view, and repository state from this browser?')) return;
    setClearing(true);
    try {
      await localRepository.deleteAllLocalData();
      setError(null);
    } catch (value) {
      setError(value instanceof Error ? value.message : 'Could not clear the local library.');
    } finally {
      setClearing(false);
    }
  }

  return <div className="page-width content-page">
    <LibraryNav/>
    <div className="page-heading library-heading">
      <div>
        <p className="eyebrow">Offline capture inventory</p>
        <h1>Immutable local captures</h1>
        <p>These authoritative local records and private Owl crops feed the rebuildable Market view.</p>
      </div>
      <div className="heading-actions">
        <Link className="button primary" to="/import">Import screenshots</Link>
        {captures.length > 0 && <button className="button secondary" type="button" disabled={clearing} onClick={() => void clearLibrary()}>{clearing ? 'Clearing…' : 'Clear local data'}</button>}
      </div>
    </div>

    {error && <div className="notice bad">{error} <button className="text-button" type="button" onClick={() => setRefreshToken((value) => value + 1)}>Retry</button></div>}

    {loading ? <section className="empty-state panel"><div className="working centered-working"><span className="spinner"/>Opening the local repository…</div></section>
      : captures.length === 0 ? <section className="empty-state panel">
        <span className="empty-mark">◎</span>
        <h2>No local captures yet</h2>
        <p>Import opaque PNG screenshots to create atomic capture, private evidence, candidate revision, sync-state, and processing-queue records.</p>
        <Link className="button primary" to="/import">Import screenshots</Link>
      </section>
        : <>
          <div className="library-summary">
            <div><strong>{captures.length}</strong><span>unique source capture{captures.length === 1 ? '' : 's'}</span></div>
            <div><strong>{captures.filter((capture) => capture.partial).length}</strong><span>partial transcription{captures.filter((capture) => capture.partial).length === 1 ? '' : 's'}</span></div>
            <div><strong>{captures.filter((capture) => capture.syncStatus === 'local-only').length}</strong><span>pending revision{captures.filter((capture) => capture.syncStatus === 'local-only').length === 1 ? '' : 's'}</span></div>
          </div>

          <section className="panel library-panel">
            <div className="read-table-wrap library-table-wrap">
              <table className="read-table library-table">
                <thead><tr><th>Sequence</th><th>Observed</th><th>Item</th><th>Page</th><th>Results</th><th>Listings</th><th>State</th><th>Source</th></tr></thead>
                <tbody>{captures.map((capture) => <tr key={capture.sourceImageId}>
                  <td>#{capture.localSequence}</td>
                  <td>{dateTime.format(capture.observedAt)}</td>
                  <td><strong>{capture.itemName ?? 'Unreadable item'}</strong>{capture.fileName && <small className="cell-subtitle">{capture.fileName}</small>}</td>
                  <td>{capture.page ?? '—'}/{capture.totalPages ?? '—'}</td>
                  <td>{capture.totalResults?.toLocaleString() ?? '—'}</td>
                  <td>{capture.listingCount}</td>
                  <td><div className="state-stack"><StatusPill tone={capture.partial ? 'warn' : 'good'}>{capture.partial ? 'Partial' : 'Complete'}</StatusPill><StatusPill tone={capture.syncStatus === 'rejected' ? 'warn' : 'neutral'}>{capture.syncStatus}</StatusPill></div></td>
                  <td><code title={capture.sourceImageId}>{shortId(capture.sourceImageId)}</code></td>
                </tr>)}</tbody>
              </table>
            </div>
          </section>
        </>}
  </div>;
}
